import Form from 'domains/models/form';
import { formatToISO } from 'infras/datetime';
import { schemaFormUserListItem, schemaFormUserUpdate } from '../form_user';
import { z } from 'zod';
import { schemaWorkflowItem } from '../workflow';
import { PlanIdEnum } from '../subscription/enum';
import {
  DisplayTypeEnum,
  FormTypeEnum,
  HostAssignTypeEnum,
  HostOperationTypeEnum,
  ListViewTypeEnum,
  MailSenderTypeEnum,
  RoundRobinAllocateTypeEnum,
  StatusEnum,
  VisualViewTypeEnum,
} from './enum';

export enum SortType {
  RecentUsed = 'recentUsed',
}

export type FormCreateBody = z.infer<typeof schemaFormCreateBody>;
export const schemaFormCreateBody = z.object({
  name: z.string(),
  description: z.string().nullable(),
  formType: FormTypeEnum,
  workflows: z.array(schemaWorkflowItem),
  isMailIncludeForm: z.boolean(),
  hostOperationType: HostOperationTypeEnum,
});

export type FormCreateResult = z.infer<typeof schemaFormCreateResult>;
export const schemaFormCreateResult = z.object({
  uid: z.string(),
});

export type FormIntegrationFormUpdateBody = z.infer<
  typeof schemaFormIntegrationFormUpdateBody
>;
export const schemaFormIntegrationFormUpdateBody = z.object({
  googleAnalyticsId: z.string().nullable().optional(),
});

export type FormUpdateBody = z.infer<typeof schemaFormUpdateBody>;
export const schemaFormUpdateBody = z.object({
  projectId: z.number().nullable().optional(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  status: StatusEnum.optional(),
  color: z.string().optional(),
  coverImage: z.string().nullable().optional(),
  workflows: z.array(schemaWorkflowItem).optional(),
  displayType: DisplayTypeEnum.optional(),
  isEnableGuestUpdate: z.boolean().optional(),
  isMailConfirm: z.boolean().optional(),
  isMailConfirmToHost: z.boolean().optional(),
  mailSenderType: MailSenderTypeEnum.optional(),
  mailConfirmTitle: z.string().nullable().optional(),
  mailConfirmBody: z.string().nullable().optional(),
  listViewType: ListViewTypeEnum.optional(),
  visualViewType: VisualViewTypeEnum.optional(),
  roundRobinAllocateType: RoundRobinAllocateTypeEnum.optional(),
  hostOperationType: HostOperationTypeEnum.optional(),
  hostAssignType: HostAssignTypeEnum.optional(),
  isMailIncludeForm: z.boolean().optional(),
  formUsers: z.array(schemaFormUserUpdate).optional(),
});

export type FormCoverImageUpdateResult = z.infer<
  typeof schemaFormCoverImageUpdateResult
>;
export const schemaFormCoverImageUpdateResult = z.object({
  coverImage: z.string().nullable(),
});

export type FormItem = z.infer<typeof schemaFormItem>;
export const schemaFormItem = z.object({
  id: z.number(),
  teamId: z.number(),
  formType: FormTypeEnum,
  displayType: DisplayTypeEnum,
  hostAssignType: HostAssignTypeEnum,
  hostOperationType: HostOperationTypeEnum,
  uid: z.string(),
  projectId: z.number().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  status: StatusEnum,
  color: z.string(),
  coverImage: z.string().nullable(),
  workflows: z.array(schemaWorkflowItem),
  users: z.array(schemaFormUserListItem),
  isMailConfirm: z.boolean(),
  isMailConfirmToHost: z.boolean(),
  isEnableGuestUpdate: z.boolean(),
  isMailIncludeForm: z.boolean(),
  mailSenderType: MailSenderTypeEnum,
  mailConfirmTitle: z.string().nullable(),
  mailConfirmBody: z.string().nullable(),
  listViewType: ListViewTypeEnum,
  visualViewType: VisualViewTypeEnum,
  roundRobinAllocateType: RoundRobinAllocateTypeEnum,
  answerCount: z.number(),
  answerUpdatedAt: z.string().nullable(),
  googleAnalyticsId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type FormPageItem = z.infer<typeof schemaFormPageItem>;
export const schemaFormPageItem = z.object({
  id: z.number(),
  teamId: z.number(),
  uid: z.string(),
  formType: FormTypeEnum,
  projectId: z.number().nullable(),
  displayType: DisplayTypeEnum,
  name: z.string(),
  description: z.string().nullable(),
  status: StatusEnum,
  color: z.string(),
  coverImage: z.string().nullable(),
  workflows: z.array(schemaWorkflowItem),
  planId: PlanIdEnum,
  isShowPromotion: z.boolean(),
  isShowHeaderTeamImage: z.boolean(),
  googleAnalyticsId: z.string().nullable(),
  visualViewType: VisualViewTypeEnum,
  listViewType: ListViewTypeEnum,
  updatedAt: z.string(),
  users: z.array(schemaFormUserListItem),
  isEnableGuestUpdate: z.boolean(),
  isAvailableRoutingForm: z.boolean(),
  metaPixelId: z.string().nullable(),
  customTag: z.string().nullable(),
});

export type FormListItem = z.infer<typeof schemaFormListItem>;
export const schemaFormListItem = z.object({
  id: z.number(),
  teamId: z.number(),
  formType: FormTypeEnum,
  displayType: DisplayTypeEnum,
  uid: z.string(),
  projectId: z.number().nullable(),
  name: z.string(),
  status: StatusEnum,
  color: z.string(),
  users: z.array(schemaFormUserListItem),
  answerCount: z.number(),
  answerUpdatedAt: z.string().nullable(),
  listViewType: ListViewTypeEnum,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type FormMinItem = z.infer<typeof schemaFormMinItem>;
export const schemaFormMinItem = z.object({
  id: z.number().nullable(),
  teamId: z.number(),
  projectId: z.number().nullable(),
  formType: FormTypeEnum,
  displayType: DisplayTypeEnum,
  status: StatusEnum,
  uid: z.string(),
  name: z.string(),
  color: z.string(),
  coverImage: z.string().nullable(),
  workflows: z.array(schemaWorkflowItem),
  description: z.string().nullable(),
  hostOperationType: HostOperationTypeEnum,
  hostAssignType: HostAssignTypeEnum,
  isMailConfirm: z.boolean(),
  isMailConfirmToHost: z.boolean(),
  isMailIncludeForm: z.boolean(),
  mailSenderType: MailSenderTypeEnum,
  mailConfirmTitle: z.string().nullable(),
  mailConfirmBody: z.string().nullable(),
  visualViewType: VisualViewTypeEnum,
  listViewType: ListViewTypeEnum,
  roundRobinAllocateType: RoundRobinAllocateTypeEnum,
  googleAnalyticsId: z.string().nullable(),
  answerUpdatedAt: z.string().nullable(),
  answerCount: z.number(),
  isEnableGuestUpdate: z.boolean(),
});

export const toPageItem = (form: Form) => {
  let workflows = form.workflows;
  if (typeof workflows === 'string') {
    workflows = JSON.parse(workflows) || [];
  }
  return {
    id: form.id!,
    teamId: form.teamId,
    formType: form.formType,
    displayType: form.displayType,
    hostAssignType: form.hostAssignType,
    hostOperationType: form.hostOperationType,
    uid: form.uid,
    projectId: form.projectId,
    name: form.name,
    description: form.description,
    coverImage: form.coverImage,
    color: form.color,
    status: form.status,
    workflows: workflows,
    isMailConfirm: form.isMailConfirm,
    isMailConfirmToHost: form.isMailConfirmToHost,
    isEnableGuestUpdate: form.isEnableGuestUpdate,
    isMailIncludeForm: form.isMailIncludeForm,
    mailSenderType: form.mailSenderType,
    visualViewType: form.visualViewType,
    listViewType: form.listViewType,
    googleAnalyticsId: form.googleAnalyticsId,
    createdAt: formatToISO(form.createdAt),
    updatedAt: formatToISO(form.updatedAt),
  };
};

export const toItem = (form: Form) => {
  let workflows = form.workflows;
  if (typeof workflows === 'string') {
    workflows = JSON.parse(workflows) || [];
  }
  return {
    id: form.id!,
    teamId: form.teamId,
    formType: form.formType,
    displayType: form.displayType,
    hostAssignType: form.hostAssignType,
    hostOperationType: form.hostOperationType,
    uid: form.uid,
    projectId: form.projectId,
    name: form.name,
    description: form.description,
    coverImage: form.coverImage,
    color: form.color,
    status: form.status,
    workflows: workflows,
    isMailConfirm: form.isMailConfirm,
    isMailConfirmToHost: form.isMailConfirmToHost,
    isEnableGuestUpdate: form.isEnableGuestUpdate,
    isMailIncludeForm: form.isMailIncludeForm,
    mailSenderType: form.mailSenderType,
    mailConfirmTitle: form.mailConfirmTitle,
    mailConfirmBody: form.mailConfirmBody,
    visualViewType: form.visualViewType,
    listViewType: form.listViewType,
    roundRobinAllocateType: form.roundRobinAllocateType,
    answerCount: form.answerCount || 0,
    googleAnalyticsId: form.googleAnalyticsId,
    answerUpdatedAt: form.answerUpdatedAt
      ? formatToISO(form.answerUpdatedAt)
      : null,
    createdAt: formatToISO(form.createdAt),
    updatedAt: formatToISO(form.updatedAt),
  };
};

export const toListItem = (form: Form) => {
  return {
    id: form.id!,
    teamId: form.teamId,
    formType: form.formType,
    displayType: form.displayType,
    uid: form.uid,
    projectId: form.projectId,
    name: form.name,
    color: form.color,
    status: form.status,
    listViewType: form.listViewType,
    answerCount: form.answerCount || 0,
    answerUpdatedAt: form.answerUpdatedAt
      ? formatToISO(form.answerUpdatedAt)
      : null,
    createdAt: formatToISO(form.createdAt),
    updatedAt: formatToISO(form.updatedAt),
  };
};
